/**
 * The public booking page talking to the embed script (public/embed.js) on a
 * merchant's site (4.5). Three messages, none carrying anything about the
 * booker: the page's height, "a booking was confirmed", and — only when a
 * popup blocker refused the deposit tab — the pay page to go to. Sent to '*'
 * because the host page's origin is the merchant's and unknown here — which
 * is exactly why nothing private (no manage or join link) is ever in them.
 */
import { useEffect } from 'react';

export type EmbedMessage =
  | { type: 'buzzbit:resize'; height: number }
  | { type: 'buzzbit:booked' }
  | { type: 'buzzbit:checkout'; url: string };

/** `?embed=1` inside a frame. */
export function isEmbedded(search: string = window.location.search): boolean {
  if (window.parent === window) return false;
  return new URLSearchParams(search).get('embed') === '1';
}

export function postToParent(message: EmbedMessage): void {
  if (window.parent === window) return;
  window.parent.postMessage(message, '*');
}

/** Keeps the host iframe as tall as the page, so the booker never scrolls inside it. */
export function useEmbedResize(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return undefined;
    const send = (): void => postToParent({ type: 'buzzbit:resize', height: document.documentElement.scrollHeight });
    const observer = new ResizeObserver(send);
    observer.observe(document.body);
    send();
    return () => observer.disconnect();
  }, [enabled]);
}

/**
 * A deposit checkout from inside a frame. The pay page (/pay/<token>) refuses
 * to render in someone else's frame, so it opens in a new tab; when a popup
 * blocker refuses (the click was before the booking request), the embed script
 * on the host page is asked to take the whole window there instead — it only
 * ever follows a /pay/ URL on its own origin.
 */
export function openCheckout(payUrl: string): void {
  // Not the 'noopener' feature: with it window.open always returns null, and
  // a blocked popup could not be told from an opened one.
  const opened = window.open(payUrl, '_blank');
  if (opened === null) {
    postToParent({ type: 'buzzbit:checkout', url: payUrl });
    return;
  }
  opened.opener = null;
}
